import { Box, Typography, LinearProgress, Button, CircularProgress } from '@mui/material';
import { connect } from 'react-redux';
import { StyledTable } from '../../components';
import { AddDefectButton } from '../../components/TableActionBar/TableActionBar';
import DefectModal from '../../components/ReportModal/DefectModal';
import styles from './Engraving.module.scss';
import {
	DARK_MODE,
	DEFAULT_COLUMNS,
	ERROR_MESSAGE,
	COLUMNS_TO_HIDE_FOR_CHASSIS_LINE,
	CHASIS_LINE_IDS,
	DEV_MODE,
	DEV_SERIAL_NUMBER,
	OPERATIONS,
	REDIRECT_URLS,
	isPuneJlr,
} from '../../constants';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { engravingInitiate, skipEngraving } from '../../redux/actionCreators/platePunching';
import { useEffect, useMemo, useState, useRef } from 'react';
import { updateOperations } from '../../redux/actionCreators/operations';
import { updateCurrentLineItem } from '../../redux/actionCreators/station';
import { getNextOperation } from '../../utils/operationFlow';
import { useRedirectToNextScreen } from '../../hooks';
import { useOutletContext, useNavigate } from 'react-router-dom';
import ReLogin from '../../components/ReLogin/ReLogin';
import {
	completeEngravingProcessValidation,
	getProcessValidationDetailsForSerialNumber,
} from '../../redux/actionCreators/processValidation';
import ReasonAndRemarkModal from '../../components/ReportModal/ReasonAndRemarkModal';
import { axiosInstance } from '../../api/api';
import { encryptStationPassword } from '../../utils/EncryptionUtils';
import { markComplete } from '../../redux/actionCreators/printBarcode';


// ─── Readiness Badge Component ────────────────────────────────────────────────
const ReadinessBadge = ({ status }) => {
	if (!status) return null;

	const config = {
		OK: {
			bg: '#e6f4ed',
			border: '#2e7d52',
			dot: '#2e7d52',
			text: '#2e7d52',
			label: 'OK',
		},
		NOK: {
			bg: '#fdecea',
			border: '#c62828',
			dot: '#c62828',
			text: '#c62828',
			label: 'NOK',
		},
		PENDING: {
			bg: '#fff8e1',
			border: '#f9a825',
			dot: '#f9a825',
			text: '#f9a825',
			label: 'Pending',
		},
	};

	const c = config[status] || config.PENDING;

	return (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 0.6,
				px: 1.2,
				py: 0.35,
				borderRadius: '999px',
				backgroundColor: c.bg,
				border: `1.5px solid ${c.border}`,
				flexShrink: 0,
			}}
		>
			<Box
				sx={{
					width: 9,
					height: 9,
					borderRadius: '50%',
					backgroundColor: c.dot,
					flexShrink: 0,
				}}
			/>
			<Typography
				sx={{
					fontSize: '11px',
					fontWeight: 700,
					lineHeight: 1,
					color: c.text,
				}}
			>
				{c.label}
			</Typography>
		</Box>
	);
};
// ─────────────────────────────────────────────────────────────────────────────


// Checks machines 50001 / 50002 / 440 for a successful VIN marking tag
const extractIsVinMarkingComplete = (data) =>
	data?.machineData
		?.find((machine) => machine?.machineId === 50001)
		?.processParameters
		?.some(
			(param) =>
				param?.key === 'VIN_MARKING_COMPLETE_TO_MES' && param?.status === 'OK'
		) ||
	data?.machineData
		?.find((machine) => machine?.machineId === 50002)
		?.processParameters
		?.some(
			(param) =>
				(param?.key === 'VIN_NO_FROM_SCADA' || param?.tagName === 'VIN_NO_FROM_SCADA') &&
				param?.status === 'OK'
		) ||
	data?.machineData
		?.find((machine) => machine?.machineId === 440)
		?.processParameters
		?.some(
			(param) =>
				(param?.key === 'VIN_NO_FROM_SCADA' || param?.tagName === 'VIN_NO_FROM_SCADA') &&
				param?.status === 'OK'
		);


const mapStateToProps = (state) => {
	return {
		partValidation: state.partValidation,
		currentLineItem: state.station.currentLineItem,
		user: state.user,
		themeMode: state.theme.themeMode,
		allowedOperations: state.operations.allowedOperations,
		groupLeaderLogin: state.GroupLeaderLogin,
	};
};

const mapDispatchToProps = (dispatch) => ({
	engravingInitiate: (sequenceNumber, success, error) =>
		dispatch(engravingInitiate(sequenceNumber, success, error)),

	skipEngraving: (sequenceNumber, payload, success, error) =>
		dispatch(skipEngraving(sequenceNumber, payload, success, error)),

	updateOperations: (data) => dispatch(updateOperations(data)),

	updateCurrentLineItem: (currentLineItem) =>
		dispatch(updateCurrentLineItem(currentLineItem)),

	getProcessValidationDetailsForSerialNumber: (serialNumber, success, error) =>
		dispatch(getProcessValidationDetailsForSerialNumber(serialNumber, success, error)),

	completeEngravingProcessValidation: (payload, success, error) =>
		dispatch(completeEngravingProcessValidation(payload, success, error)),

	markComplete: (success, error, sequenceNumber) =>
		dispatch(markComplete(success, error, sequenceNumber)),
});

export const Engraving = ({
	engravingInitiate,
	completeEngravingProcessValidation,
	skipEngraving,
	currentLineItem,
	updateOperations,
	updateCurrentLineItem,
	user,
	themeMode,
	allowedOperations,
	groupLeaderLogin,
	getProcessValidationDetailsForSerialNumber,
	markComplete,
}) => {
	const [currentAgsnData, setCurrentAgsnData] = useState({});
	const { showMessageOverlay } = useMessageContextManager();

	const hasPartValidationPermission = allowedOperations.includes(OPERATIONS.PART_VALIDATION);
	const hasMachinePermission = allowedOperations.includes(OPERATIONS.MACHINE);
	const redirect = useRedirectToNextScreen();
	const navigate = useNavigate();
	const { handleNext } = useOutletContext();
	const [loading, setLoading] = useState(false);

	// ── Engraving progress / status ───────────────────────────────────────────
	const [isEngravingInProgress, setIsEngravingInProgress] = useState(false);
	const [progress, setProgress] = useState(0);
	const [engravingStatus, setEngravingStatus] = useState(null);
	const [showSkipAndMarkComplete, setShowSkipAndMarkComplete] = useState(false);

	// ── Skip / Fail Forward modal states ─────────────────────────────────────
	const [showReLoginModal, setShowReLoginModal] = useState(false);
	const [showSkipRemarksModal, setShowSkipRemarksModal] = useState(false);
	const [groupLeaderCredentials, setGroupLeaderCredentials] = useState({ username: '', password: '' });

	// ── Defect modal ──────────────────────────────────────────────────────────
	const [isDefectModalOpen, setIsDefectModalOpen] = useState(false);

	// ── READINESS badge state ─────────────────────────────────────────────────
	const [readinessStatus, setReadinessStatus] = useState(null); // 'OK' | 'NOK' | 'PENDING'
	const readinessPollingRef = useRef(null);

	// ── Guard: polling only starts after Initiate Engraving is clicked ────────
	const [engravingInitiated, setEngravingInitiated] = useState(false);

	// ── CRTD / GEN states ─────────────────────────────────────────────────────
	// crtdStatus: null (not yet attempted) | 'SUCCESS' | 'FAILED'
	const [crtdStatus, setCrtdStatus] = useState(null);
	const [isCrtdLoading, setIsCrtdLoading] = useState(false);

	// ── Bypass CRTD privileged modal ──────────────────────────────────────────
	const [showBypassCrtdModal, setShowBypassCrtdModal] = useState(false);

	// ── Timer refs ────────────────────────────────────────────────────────────
	const progressTimerRef = useRef(null);
	const statusCheckTimerRef = useRef(null);
	const pollingIntervalRef = useRef(null);
	const engravingTriggeredRef = useRef(false);
	// Tracks whether the machine sent any machineData during the current engraving session.
	// initiate-crtd must NOT be called if the machine never responded (empty machineData).
	const hasMachineDataRef = useRef(false);
	// Screen-level 5s poll — runs independently of the Initiate button (both plants)
	const screenPollIntervalRef = useRef(null);
	// Guards against the screen poll and the Initiate-triggered poll both reacting to the same completion
	const completionHandledRef = useRef(false);
	// Guards against runCrtdThenMarkComplete being called more than once (auto + manual race)
	const completionInProgressRef = useRef(false);


	// ── API helpers ───────────────────────────────────────────────────────────

	/** Initiates CRTD for a given sequence number */
	const initiateCRTD = async (sequenceNumber) => {
		const response = await axiosInstance.post(`station/initiate-crtd/${sequenceNumber}`);
		return response.data;
	};

	/** Checks GEN status in SAP for the given serial number */
	const checkGenStatus = async (serialNumber) => {
		const response = await axiosInstance.get(`/sap-status/gen/${serialNumber}`);
		return response.data; // { serialNumber, isGenPresent: true | false }
	};

	/**
	 * Shared completion helper used by all three scenarios.
	 * 1. POST /station/process-validation/engraving/complete (both machine IDs)
	 * 2. POST /station/initiate-crtd/{sequenceNumber} (errors ignored, always continue)
	 * 3. POST /station/complete/{sequenceNumber} via markComplete
	 * 4. On success → reset state and navigate to next operation
	 */
	const runCrtdThenMarkComplete = async (sequenceNumber) => {
		if (completionInProgressRef.current) return;
		completionInProgressRef.current = true;
		completionHandledRef.current = true;

		const closeOverlay = showMessageOverlay({
			contextText: 'Completing engraving...',
			loading: true,
		});

		const serialNumber =
			currentLineItem?.serialNumber || currentAgsnData?.serialNumber;

		const engravingCompletePayloads = [
			{
				caMachineId: 50001,
				key: 'VIN_MARKING_COMPLETE_TO_MES',
				description: 'Engraving completed',
				value: serialNumber,
				unit: 'STRING',
				status: true,
				serialNumber,
				sequence: 1,
			},
			{
				caMachineId: 50002,
				key: 'VIN_NO_FROM_SCADA',
				description: 'Engraving completed',
				value: serialNumber,
				unit: 'STRING',
				status: true,
				serialNumber,
				sequence: 1,
			},
			{
				caMachineId: 440,
				key: 'VIN_NO_FROM_SCADA',
				description: 'Engraving completed',
				value: serialNumber,
				unit: 'STRING',
				status: true,
				serialNumber,
				sequence: 1,
			},
		];

		for (const payload of engravingCompletePayloads) {
			await new Promise((resolve) => {
				completeEngravingProcessValidation(
					payload,
					() => resolve(),
					(err) => {
						console.error(`Engraving complete API error for machine ${payload.caMachineId} (proceeding):`, err);
						resolve();
					}
				);
			});
		}

		try {
			await initiateCRTD(sequenceNumber);
		} catch (e) {
			console.error('CRTD error (proceeding with mark complete):', e);
		}

		const updatedCompletedOps = [
			...(currentLineItem?.completedOperations || []),
			OPERATIONS.ENGRAVING,
		];

		markComplete(
			() => {
				completionInProgressRef.current = false;
				closeOverlay();
				updateCurrentLineItem({ ...currentLineItem, completedOperations: updatedCompletedOps });
				setShowSkipAndMarkComplete(false);
				setEngravingStatus(null);
				setProgress(0);
				setCrtdStatus(null);
				showMessageOverlay({ contextText: 'Station Completed Successfully', success: true });
				setTimeout(() => {
					const nextOp = getNextOperation(updatedCompletedOps, allowedOperations);
					updateOperations({ currentOperation: nextOp });
					redirect(() => navigate(REDIRECT_URLS[nextOp] || '/scan-vin'));
				}, 2000);
			},
			(err) => {
				completionInProgressRef.current = false;
				closeOverlay();
				showMessageOverlay({
					contextText: ERROR_MESSAGE.MARK_COMPLETE_ERROR_MESSAGE,
					info: err?.response?.data?.message || 'Station complete failed.',
					error: true,
				});
			},
			sequenceNumber,
		);
	};

	// ─────────────────────────────────────────────────────────────────────────


	// ── Populate currentAgsnData from Redux ───────────────────────────────────
	useEffect(() => {
		if (currentLineItem && Object.keys(currentLineItem).length > 0) {
			setCurrentAgsnData({
				...currentLineItem,
				id: currentLineItem.sequenceNumber,
				readyForDropSequenceNumber: currentLineItem.metaData?.readyForDropSequenceNumber,
				vcDescription: currentLineItem.metaData?.vcDescription,
				vcSequenceNumber: currentLineItem.metaData?.vcSequenceNumber,
				vcPartNumber: currentLineItem.metaData?.vcPartNumber,
				vcNumber: currentLineItem.metaData?.vcNumber,
				serialNumber:
					currentLineItem.serialNumber || (DEV_MODE ? DEV_SERIAL_NUMBER : null),
			});
		}
	}, [currentLineItem]);


	// ── READINESS polling — every 10 seconds ──────────────────────────────────
	useEffect(() => {
		// READINESS badge is only applicable for Panapakkam — not shown for Pune
		if (isPuneJlr()) return;

		if (!engravingInitiated) return;

		const serialNumber =
			currentLineItem?.serialNumber ||
			currentAgsnData?.serialNumber ||
			(DEV_MODE ? DEV_SERIAL_NUMBER : null);

		if (!serialNumber) return;

		const parseReadiness = (data) => {
			const machine =
				data?.machineData?.find((m) => m?.machineId === 50001) ||
				data?.machineData?.find((m) => m?.machineId === 50002) ||
				data?.machineData?.find((m) => m?.machineId === 440);

			const found =
				machine?.processParameters?.find((p) => p?.key === 'READINESS') ||
				machine?.processParameters?.find(
					(p) => p?.key === 'VIN_NO_FROM_SCADA' || p?.tagName === 'VIN_NO_FROM_SCADA'
				);

			if (!found) {
				setReadinessStatus('PENDING');
				return;
			}

			const val = found.value;
			const st = found.status;

			if (val === 'OK' || val === true || val === 'true' || st === 'OK') {
				setReadinessStatus('OK');
			} else if (val === 'NOK' || val === false || val === 'false' || st === 'NOK') {
				setReadinessStatus('NOK');
			} else {
				setReadinessStatus('PENDING');
			}
		};

		const fetchReadiness = () => {
			getProcessValidationDetailsForSerialNumber(
				serialNumber,
				(data) => parseReadiness(data),
				(err) => console.error('Readiness polling error:', err)
			);
		};

		fetchReadiness();
		readinessPollingRef.current = setInterval(fetchReadiness, 10000);

		return () => {
			if (readinessPollingRef.current) {
				clearInterval(readinessPollingRef.current);
				readinessPollingRef.current = null;
			}
		};
	}, [currentLineItem?.serialNumber, currentAgsnData?.serialNumber, engravingInitiated]);
	// ─────────────────────────────────────────────────────────────────────────


	// ── Shared "VIN marking tag found" handler ────────────────────────────────
	// Used by both the screen-level 5s poll and the Initiate-triggered poll.
	const handleEngravingDetected = (sequenceNumber, serialNumber) => {
		if (completionHandledRef.current) return;
		completionHandledRef.current = true;

		// Stop all polling — both Initiate-triggered and screen-level
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
		if (progressTimerRef.current) {
			clearInterval(progressTimerRef.current);
			progressTimerRef.current = null;
		}
		if (statusCheckTimerRef.current) {
			clearTimeout(statusCheckTimerRef.current);
			statusCheckTimerRef.current = null;
		}
		if (screenPollIntervalRef.current) {
			clearInterval(screenPollIntervalRef.current);
			screenPollIntervalRef.current = null;
		}
		engravingTriggeredRef.current = false;

		setProgress(100);
		setIsEngravingInProgress(false);

		// Tag found → complete directly, no GEN/SAP check needed.
		setShowSkipAndMarkComplete(false);
		setEngravingStatus('SUCCESS');
		runCrtdThenMarkComplete(sequenceNumber);
	};
	// ─────────────────────────────────────────────────────────────────────────


	// ── Screen-level polling — every 5 seconds, both plants ───────────────────
	// Only starts after the operator clicks Initiate Engraving.
	useEffect(() => {
		if (!engravingInitiated) return;

		const serialNumber =
			currentLineItem?.serialNumber ||
			currentAgsnData?.serialNumber ||
			(DEV_MODE ? DEV_SERIAL_NUMBER : null);

		if (!serialNumber) return;

		completionHandledRef.current = false;

		const poll = () => {
			if (completionHandledRef.current) return;

			getProcessValidationDetailsForSerialNumber(
				serialNumber,
				(data) => {
					if (completionHandledRef.current) return;

					if (data?.machineData?.length > 0) {
						hasMachineDataRef.current = true;
					}

					if (extractIsVinMarkingComplete(data)) {
						const sequenceNumber =
							currentLineItem?.sequenceNumber || currentAgsnData?.sequenceNumber;
						handleEngravingDetected(sequenceNumber, serialNumber);
					}
				},
				(err) => console.error('Engraving screen polling error:', err)
			);
		};

		poll();
		screenPollIntervalRef.current = setInterval(poll, 5000);

		return () => {
			if (screenPollIntervalRef.current) {
				clearInterval(screenPollIntervalRef.current);
				screenPollIntervalRef.current = null;
			}
		};
	}, [currentLineItem?.serialNumber, currentAgsnData?.serialNumber, engravingInitiated]);
	// ─────────────────────────────────────────────────────────────────────────


	const filteredOriginalDefaultColumns = useMemo(() => {
		if (CHASIS_LINE_IDS.includes(user?.lineId)) {
			return DEFAULT_COLUMNS.filter(
				(res) => !COLUMNS_TO_HIDE_FOR_CHASSIS_LINE.includes(res.field)
			);
		}
		return DEFAULT_COLUMNS;
	}, [DEFAULT_COLUMNS, user?.lineId]);


	// ── Cleanup ALL timers on unmount ─────────────────────────────────────────
	useEffect(() => {
		return () => {
			if (progressTimerRef.current) clearInterval(progressTimerRef.current);
			if (statusCheckTimerRef.current) clearTimeout(statusCheckTimerRef.current);
			if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
			if (readinessPollingRef.current) clearInterval(readinessPollingRef.current);
			if (screenPollIntervalRef.current) clearInterval(screenPollIntervalRef.current);
		};
	}, []);


	// ── Initiate Engraving ────────────────────────────────────────────────────
	const handleInitialEngraving = () => {
		if (engravingTriggeredRef.current) return;
		engravingTriggeredRef.current = true;
		setEngravingInitiated(true);

		const sequenceNumber =
			currentLineItem?.sequenceNumber || currentAgsnData?.sequenceNumber;

		if (!sequenceNumber) {
			engravingTriggeredRef.current = false;
			showMessageOverlay({
				contextText: 'Engraving Initiation Error',
				info: 'Sequence number not available to initiate engraving.',
				error: true,
			});
			return;
		}

		const serialNumber = currentAgsnData?.serialNumber;
		if (!serialNumber) {
			engravingTriggeredRef.current = false;
			showMessageOverlay({
				contextText: 'Engraving Initiation Error',
				info: 'Serial number not available to initiate engraving.',
				error: true,
			});
			return;
		}

		// Reset state — also reset CRTD status on fresh engraving attempt
		setIsEngravingInProgress(true);
		setProgress(0);
		setEngravingStatus(null);
		setShowSkipAndMarkComplete(false);
		setCrtdStatus(null);
		hasMachineDataRef.current = false;
		completionHandledRef.current = false;

		const startTime = Date.now();
		const duration = 1800;
		let pollingStopped = false;

		// Progress timer
		progressTimerRef.current = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const newProgress = Math.min((elapsed / duration) * 100, 100);
			setProgress(newProgress);
			if (newProgress >= 100) clearInterval(progressTimerRef.current);
		}, 100);

		// ── Helper: stop all timers ───────────────────────────────────────────
		const stopAllTimers = () => {
			pollingStopped = true;
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
			if (progressTimerRef.current) {
				clearInterval(progressTimerRef.current);
				progressTimerRef.current = null;
			}
			if (statusCheckTimerRef.current) {
				clearTimeout(statusCheckTimerRef.current);
				statusCheckTimerRef.current = null;
			}
		};

		// ── Process validation status check (VIN_MARKING_COMPLETE or VIN_NO_FROM_SCADA) ──
		const checkProcessValidationStatus = () => {
			if (pollingStopped) return;

			getProcessValidationDetailsForSerialNumber(
				serialNumber,
				(data) => {
					if (pollingStopped) return;

					// Track whether the machine has sent any data at all
					if (data?.machineData?.length > 0) {
						hasMachineDataRef.current = true;
					}

					if (extractIsVinMarkingComplete(data)) {
						stopAllTimers();
						handleEngravingDetected(sequenceNumber, serialNumber);
					}
				},
				(error) => {
					console.error('Error checking process validation status:', error);
				}
			);
		};

		engravingInitiate(
			sequenceNumber,
			() => {
				checkProcessValidationStatus();

				pollingIntervalRef.current = setInterval(() => {
					checkProcessValidationStatus();
				}, 5000);

				// ── Timeout: call CRTD first, then show Skip / Mark Complete ─────
				statusCheckTimerRef.current = setTimeout(async () => {
					// The screen-level poll may have already detected completion
					if (completionHandledRef.current) return;

					if (pollingIntervalRef.current) {
						clearInterval(pollingIntervalRef.current);
						pollingIntervalRef.current = null;
					}
					if (progressTimerRef.current) {
						clearInterval(progressTimerRef.current);
						progressTimerRef.current = null;
					}
					pollingStopped = true;
					engravingTriggeredRef.current = false;

					// Only call initiate-crtd on timeout if the machine sent data.
					// If machineData was empty throughout, skip the CRTD call entirely.
					if (hasMachineDataRef.current) {
						try {
							await initiateCRTD(sequenceNumber);
						} catch (crtdError) {
							console.error('CRTD initiation failed after timeout:', crtdError);
						}
					}

					setIsEngravingInProgress(false);
					setShowSkipAndMarkComplete(true);
				}, duration);
				// ─────────────────────────────────────────────────────────────────
			},
			(error) => {
				engravingTriggeredRef.current = false;
				pollingStopped = true;

				if (progressTimerRef.current) {
					clearInterval(progressTimerRef.current);
					progressTimerRef.current = null;
				}
				if (pollingIntervalRef.current) {
					clearInterval(pollingIntervalRef.current);
					pollingIntervalRef.current = null;
				}
				if (statusCheckTimerRef.current) {
					clearTimeout(statusCheckTimerRef.current);
					statusCheckTimerRef.current = null;
				}

				setIsEngravingInProgress(false);
				setProgress(0);

				showMessageOverlay({
					contextText: 'Engraving Initiation Failed',
					info:
						error?.response?.data?.message ||
						'Failed to initiate engraving process.',
					error: true,
				});
			}
		);
	};
	// ─────────────────────────────────────────────────────────────────────────


	// ── "Initiate CRTD" button handler ───────────────────────────────────────
	// Flow:
	//   GET /sap-status/gen/{serialNumber}
	//     → isGenPresent true  → crtdStatus = 'SUCCESS' (Mark Complete unlocked)
	//     → isGenPresent false → crtdStatus = 'FAILED'  (Retry available)
	// Note: initiate-crtd POST is NOT called from this button.
	//       It is only called automatically on timeout inside handleInitialEngraving.
	const handleInitiateCRTD = async () => {
		const sequenceNumber =
			currentLineItem?.sequenceNumber || currentAgsnData?.sequenceNumber;
		const serialNumber = currentAgsnData?.serialNumber;

		if (!sequenceNumber || !serialNumber) {
			showMessageOverlay({
				contextText: 'CRTD Error',
				info: 'Sequence / Serial number not available.',
				error: true,
			});
			return;
		}

		setIsCrtdLoading(true);
		let genPresent = false;

		try {
			const genData = await checkGenStatus(serialNumber);
			genPresent = genData?.isGenPresent === true;

			if (genPresent) {
				setCrtdStatus('SUCCESS');
			} else {
				setCrtdStatus('FAILED');
				showMessageOverlay({
					contextText: 'GEN Not Present',
					info: 'GEN is not yet present in SAP. Please retry.',
					error: true,
				});
			}
		} catch (err) {
			setCrtdStatus('FAILED');
			showMessageOverlay({
				contextText: 'CRTD Error',
				info: err?.response?.data?.message || 'Failed to check GEN status.',
				error: true,
			});
		} finally {
			setIsCrtdLoading(false);
		}

		// Scenario 2: GEN present → auto-complete (initiate-crtd → station/complete)
		if (genPresent) {
			await runCrtdThenMarkComplete(sequenceNumber);
		}
	};
	// ─────────────────────────────────────────────────────────────────────────


	// ── Mark Complete click — gate with CRTD check ────────────────────────────
	// crtdStatus === 'SUCCESS' → proceed directly
	// Pune                    → no password interlock, proceed directly
	// otherwise (Panapakkam)  → open privileged bypass login modal
	const handleMarkCompleteClick = () => {
		if (crtdStatus === 'SUCCESS') {
			// GEN was already confirmed — retry completion without label call
			const sequenceNumber =
				currentLineItem?.sequenceNumber || currentAgsnData?.sequenceNumber;
			if (sequenceNumber) runCrtdThenMarkComplete(sequenceNumber);
		} else if (isPuneJlr()) {
			// Pune has no password interlock for manual mark complete
			handleMarkCompleteForNonSuccess();
		} else {
			// Scenario 3: open privileged login modal
			setShowBypassCrtdModal(true);
		}
	};
	// ─────────────────────────────────────────────────────────────────────────


	// ── Bypass CRTD privileged auth ───────────────────────────────────────────
	// Called from the bypass ReLogin modal.
	// POST /station/perform-privileged-action/privileged-auth/bypass-crtd/{seq}/{serial}
	// Regardless of 200 or error → always proceed with mark complete.
	const handleBypassCrtdReLogin = async (e, username, password) => {
		e.preventDefault();
		const encryptedPassword = encryptStationPassword(password);
		const sequenceNumber =
			currentLineItem?.sequenceNumber || currentAgsnData?.sequenceNumber;
		const serialNumber = currentAgsnData?.serialNumber;

		const closeOverlay = showMessageOverlay({
			contextText: 'Authenticating...',
			loading: true,
		});

		try {
			await axiosInstance.post(
				`/station/perform-privileged-action/privileged-auth/bypass-crtd/${sequenceNumber}/${serialNumber}`,
				{
					username,
					password: encryptedPassword,
					action: 'CRTD_OVERRIDE',
					remark: 'Bypassing CRTD for manual mark complete',
				}
			);
			closeOverlay();
			showMessageOverlay({
				contextText: 'Privileged Auth Successful',
				success: true,
			});
		} catch (err) {
			// Per requirement — even on error, proceed with mark complete
			closeOverlay();
			console.warn(
				'Bypass CRTD API error (proceeding anyway):',
				err?.response?.data?.message
			);
		} finally {
			setShowBypassCrtdModal(false);
			handleMarkCompleteForNonSuccess();
		}
	};
	// ─────────────────────────────────────────────────────────────────────────


	// ── Skip Engraving (Fail Forward) ─────────────────────────────────────────
	const handleSkipEngraving = () => {
		setShowReLoginModal(true);
	};

	const handleReLoginSuccess = async (e, username, password) => {
		e.preventDefault();
		const encryptedPassword = encryptStationPassword(password);

		setGroupLeaderCredentials({ username, password: encryptedPassword });

		try {
			const body = {
				username,
				password: encryptedPassword,
				action: 'GROUP_LEADER',
				remark: 'skip engraving',
			};

			const response = await axiosInstance.post(
				`/station/perform-privileged-action/wis/listSubfoldersAndFiles?path=open/`,
				body
			);

			if (response.status === 200) {
				setGroupLeaderCredentials({ username, password });
				showMessageOverlay({ contextText: 'Authentication Successful', success: true });
				setShowReLoginModal(false);
				setShowSkipRemarksModal(true);
			} else {
				showMessageOverlay({
					contextText: 'Authentication Failed',
					info: 'Invalid Group Leader credentials',
					error: true,
				});
				setShowReLoginModal(true);
			}
		} catch (err) {
			showMessageOverlay({
				contextText: 'Authentication Error',
				info: err?.response?.data?.message || err.message,
				error: true,
			});
			setShowReLoginModal(true);
		}
	};

	const handleSkipRemarksSubmit = (reason, remarks) => {
		const sequenceNumber =
			currentLineItem?.sequenceNumber || currentAgsnData?.sequenceNumber;

		if (!sequenceNumber) {
			showMessageOverlay({
				contextText: 'Skip Engraving Error',
				info: 'Sequence number not available to skip engraving.',
				error: true,
			});
			setShowSkipRemarksModal(false);
			return;
		}

		const { username, password } = groupLeaderCredentials.username
			? groupLeaderCredentials
			: groupLeaderLogin || {};

		if (!username || !password) {
			showMessageOverlay({
				contextText: 'Authentication Error',
				info: 'Group leader credentials not available.',
				error: true,
			});
			setShowSkipRemarksModal(false);
			return;
		}

		skipEngraving(
			sequenceNumber,
			{ username, password, reason, remarks },
			() => {
				setShowSkipRemarksModal(false);
				setShowSkipAndMarkComplete(false);
				setEngravingStatus(null);
				setProgress(0);
				setCrtdStatus(null);

				showMessageOverlay({
					contextText: 'Engraving Skipped Successfully',
					info: 'The engraving operation has been skipped. Proceeding to next operation.',
					success: true,
				});

				const updatedCompletedOperations = [
					...(currentLineItem?.completedOperations || []),
					OPERATIONS.ENGRAVING,
				];

				updateCurrentLineItem({
					...currentLineItem,
					completedOperations: updatedCompletedOperations,
				});

				const nextOperation = getNextOperation(
					updatedCompletedOperations,
					allowedOperations
				);
				updateOperations({ currentOperation: nextOperation });

				if (nextOperation) {
					const nextPath = REDIRECT_URLS[nextOperation] || '/scan-vin';
					redirect(() => navigate(nextPath));
				} else {
					redirect(() => navigate('/scan-vin'));
				}
			},
			(error) => {
				showMessageOverlay({
					contextText: 'Skip Engraving Failed',
					info:
						error?.response?.data?.message ||
						'Failed to skip engraving operation.',
					error: true,
				});
				setShowSkipRemarksModal(false);
			}
		);
	};
	// ─────────────────────────────────────────────────────────────────────────


	// ── Mark Complete (Scenario 3 — privileged bypass) ───────────────────────
	// Sequence:
	//   1. POST /station/label/ENGRAVE/{sequenceNumber}
	//   2. POST /station/initiate-crtd/{sequenceNumber}  (via runCrtdThenMarkComplete)
	//   3. POST /station/complete/{sequenceNumber}        (via runCrtdThenMarkComplete)
	const handleMarkCompleteForNonSuccess = async () => {
		const sequenceNumber =
			currentLineItem?.sequenceNumber || currentAgsnData?.sequenceNumber;

		if (!sequenceNumber) {
			showMessageOverlay({
				contextText: ERROR_MESSAGE.MARK_COMPLETE_ERROR_MESSAGE,
				info: 'Sequence number not available.',
				error: true,
			});
			return;
		}

		const closeLabelOverlay = showMessageOverlay({
			contextText: 'Posting engraving label...',
			loading: true,
		});

		try {
			await axiosInstance.get(`/station/label/ENGRAVE/${sequenceNumber}`);
		} catch (e) {
			console.error('Label ENGRAVE error (proceeding):', e);
		} finally {
			closeLabelOverlay();
		}

		await runCrtdThenMarkComplete(sequenceNumber);
	};
	// ─────────────────────────────────────────────────────────────────────────


	// ── Derived helpers for CRTD button label / color ─────────────────────────
	const crtdButtonLabel = isCrtdLoading
		? 'Checking...'
		: crtdStatus === 'SUCCESS'
			? '✓ GEN Verified'
			: crtdStatus === 'FAILED'
				? 'Retry GEN Check'
				: 'GEN SAP CHECK';

	const crtdButtonBg =
		crtdStatus === 'SUCCESS'
			? '#4caf50'
			: crtdStatus === 'FAILED'
				? '#f44336'
				: '#F57C00';

	const crtdButtonHoverBg =
		crtdStatus === 'SUCCESS'
			? '#43a047'
			: crtdStatus === 'FAILED'
				? '#d32f2f'
				: '#E65100';
	// ─────────────────────────────────────────────────────────────────────────


	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<>
			<Box
				className={`${styles.backgroundBase} ${themeMode === DARK_MODE
					? styles.backgroundBaseDark
					: styles.backgroundBaseLight
					}`}
			>
				<Box
					className={`${styles.gradientContent} ${themeMode === DARK_MODE
						? styles.gradientContentDark
						: styles.gradientContentLight
						}`}
				>
					{/* Top data table */}
					<StyledTable
						title="Current KSPEC"
						listId="engraving"
						rows={[currentAgsnData]}
						columns={[...filteredOriginalDefaultColumns]}
						defaultColumns={[...filteredOriginalDefaultColumns]}
						showCheckBox={false}
						elevated={true}
						showFilter={false}
						showColumns={false}
						forceHeight={window.innerWidth >= 3840 ? 200 : 100}
					/>

					{/* Section header — title + READINESS badge + Add Defect */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							px: 2,
							pt: 2,
							pb: 1,
							flexWrap: 'wrap',
							gap: 1,
						}}
					>
						{/* Left: title + badge */}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
							<Typography
								variant="h6"
								sx={{
									color: themeMode === DARK_MODE ? '#fff' : '#000',
									fontWeight: 600,
								}}
							>
								Engraving operation
							</Typography>

							{/* READINESS STATUS BADGE — Panapakkam only */}
							{!isPuneJlr() && <ReadinessBadge status={readinessStatus} />}
						</Box>

						{/* Right: Add Defect */}
						{currentAgsnData?.serialNumber && (
							<AddDefectButton onClick={() => setIsDefectModalOpen(true)} />
						)}
					</Box>

					{/* Main content area */}
					<Box
						className={`${styles.tableContentContainer} ${themeMode === DARK_MODE
							? styles.tableContentContainerDark
							: styles.tableContentContainerLight
							}`}
					>
						{/* VIN display */}
						<Box className={styles.serialNumberSection}>
							<Typography className={styles.readyText}>
								Ready to Start Engraving Process
							</Typography>

							<Typography
								className={`${styles.agsnText} ${themeMode === DARK_MODE
									? styles.agsnTextDark
									: styles.agsnTextLight
									}`}
							>
								VIN
							</Typography>

							<Box
								className={`${styles.serialNumberBox} ${themeMode === DARK_MODE
									? styles.serialNumberBoxDark
									: styles.serialNumberBoxLight
									}`}
							>
								<Typography className={styles.serialNumberText}>
									{currentAgsnData.serialNumber}
								</Typography>
							</Box>
						</Box>
					</Box>

					{/* Progress bar — visible only when engraving is running */}
					{isEngravingInProgress && (
						<Box sx={{ width: '100%', mt: 3, mb: 2 }}>
							<Typography
								sx={{ mb: 1, textAlign: 'center', fontSize: '16px' }}
							>
								Engraving in progress... {Math.round(progress)}%
							</Typography>
							<LinearProgress
								variant="determinate"
								value={progress}
								sx={{
									height: 10,
									borderRadius: 5,
									backgroundColor:
										themeMode === DARK_MODE ? '#3A3A3A' : '#E0E0E0',
									'& .MuiLinearProgress-bar': {
										backgroundColor: '#82C1A7',
									},
								}}
							/>
						</Box>
					)}

					{/* ── Action buttons shown after timeout ── */}
					{showSkipAndMarkComplete &&
						!isEngravingInProgress && (
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 3,
									justifyContent: 'center',
									mt: 3,
									mb: 2,
								}}
							>
								{/* Fail Forward */}
								<Button
									variant="contained"
									onClick={handleSkipEngraving}
									sx={{
										backgroundColor: '#064A88',
										color: '#FFFFFF',
										fontWeight: 800,
										paddingX: 4,
										paddingY: 1.5,
										'&:hover': { backgroundColor: '#053A6E' },
									}}
								>
									Fail Forward
								</Button>

								{/* ── Check GEN Status (was: Initiate CRTD) ──────────────────── */}
								{/* Calls GET /sap-status/gen/{serialNumber} only.               */}
								{/* POST /station/initiate-crtd is NOT triggered here.           */}
								<Button
									variant="contained"
									onClick={handleInitiateCRTD}
									disabled={isCrtdLoading || crtdStatus === 'SUCCESS'}
									sx={{
										backgroundColor: crtdButtonBg,
										color: '#FFFFFF',
										fontWeight: 800,
										paddingX: 4,
										paddingY: 1.5,
										minWidth: 160,
										'&:hover': { backgroundColor: crtdButtonHoverBg },
										// Keep green even when "disabled" (done state)
										'&.Mui-disabled': {
											backgroundColor:
												crtdStatus === 'SUCCESS' ? '#4caf50' : '#BDBDBD',
											color: '#fff',
										},
									}}
								>
									{isCrtdLoading ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<CircularProgress size={16} sx={{ color: '#fff' }} />
											Checking...
										</Box>
									) : (
										crtdButtonLabel
									)}
								</Button>
								{/* ─────────────────────────────────────────────────────────────── */}

								{/* Mark Complete */}
								<Button
									variant="contained"
									onClick={handleMarkCompleteClick}
									sx={{
										backgroundColor: '#82C1A7',
										color: '#FFFFFF',
										fontWeight: 800,
										paddingX: 4,
										paddingY: 1.5,
										'&:hover': { backgroundColor: '#6BA892' },
									}}
								>
									Mark Complete
								</Button>
							</Box>
						)}

					{/* Initiate Engraving button — hidden while in-progress or showing skip/mark */}
					{!isEngravingInProgress && !showSkipAndMarkComplete && (
						<Box
							className={styles.buttonContainer}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%',
							}}
						>
							<Box
								component="button"
								onClick={handleInitialEngraving}
								disabled={!currentAgsnData.serialNumber && !DEV_MODE}
								className={styles.initiateButton}
							>
								<Typography className={styles.buttonText}>
									Initiate Engraving
								</Typography>
							</Box>
						</Box>
					)}
				</Box>
			</Box>

			{/* ── Fail Forward — Group Leader ReLogin Modal ── */}
			<ReLogin
				open={showReLoginModal}
				handleClose={() => setShowReLoginModal(false)}
				handleReLogin={handleReLoginSuccess}
				isDark={themeMode === DARK_MODE}
			/>

			{/* ── Bypass CRTD — Privileged Login Modal ── */}
			{/*   Opens when Mark Complete is clicked and crtdStatus !== 'SUCCESS'  */}
			{/*   Calls /bypass-crtd API; proceeds with mark complete either way    */}
			<ReLogin
				open={showBypassCrtdModal}
				handleClose={() => setShowBypassCrtdModal(false)}
				handleReLogin={handleBypassCrtdReLogin}
				isDark={themeMode === DARK_MODE}
			/>

			{/* Skip Remarks Modal */}
			<ReasonAndRemarkModal
				nameofModal="Skip Engraving"
				open={showSkipRemarksModal}
				handleClose={() => setShowSkipRemarksModal(false)}
				handleSubmit={handleSkipRemarksSubmit}
				data={{
					vin: currentAgsnData.serialNumber,
					partName: 'Engraving Operation',
				}}
				dropDown={[
					'MACHINE ERROR',
					'ENGRAVING FAILED',
					'NO RESPONSE FROM MACHINE',
					'MANUAL SKIP REQUIRED',
					'OTHER',
				]}
				leftTitle="VIN"
				rightTitle="Operation"
			/>

			{/* Add Defect Modal */}
			<DefectModal
				open={isDefectModalOpen}
				handleClose={() => setIsDefectModalOpen(false)}
				partDetailsRow={[
					{
						serialNumber: currentAgsnData?.serialNumber || '-',
						parentPartNumber:
							currentAgsnData?.partNumber ||
							currentAgsnData?.vcPartNumber ||
							'-',
						parentPartDescription:
							currentAgsnData?.description ||
							currentAgsnData?.vcDescription ||
							'-',
					},
				]}
				partDetails={{
					serialNumber: currentAgsnData?.serialNumber,
					sequenceNumber:
						currentLineItem?.sequenceNumber ||
						currentAgsnData?.sequenceNumber,
				}}
			/>
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(Engraving);
